/**
 * Redux / format resolution for `tvcIssuesBehavior` source.
 */

const TVC_ISSUES_BEHAVIOR_TYPE = 'tvcIssuesBehavior';

/**
 * Default native behavior the section calls after a project is chosen.
 * Override via format.tvcIssuesBehavior (or behaviors[]) when the server needs extra fields.
 */
export const DEFAULT_TVC_ISSUES_BEHAVIOR_SOURCE = Object.freeze({
  type: TVC_ISSUES_BEHAVIOR_TYPE
});
const isTvcIssuesBehaviorSource = obj => obj != null && typeof obj === 'object' && !Array.isArray(obj) && obj.type === TVC_ISSUES_BEHAVIOR_TYPE;

/**
 * @param {unknown} raw - behaviors[].source or sources[]
 * @returns {Object|null}
 */
const firstTvcIssuesInSource = raw => {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    for (let i = 0; i < raw.length; i += 1) {
      const x = raw[i];
      if (isTvcIssuesBehaviorSource(x)) {
        return x;
      }
    }
    return null;
  }
  return isTvcIssuesBehaviorSource(raw) ? raw : null;
};

/**
 * Depth-first search for an object with type tvcIssuesBehavior (template nesting, variables).
 * @param {unknown} node
 * @param {number} depth
 * @param {number} maxDepth
 * @returns {Object|null}
 */
const deepFindTvcIssuesBehaviorSource = (node, depth, maxDepth) => {
  if (depth > maxDepth || node == null || typeof node !== 'object') {
    return null;
  }
  if (isTvcIssuesBehaviorSource(node)) {
    return node;
  }
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const found = deepFindTvcIssuesBehaviorSource(node[i], depth + 1, maxDepth);
      if (found) return found;
    }
    return null;
  }
  const keys = Object.keys(node);
  for (let i = 0; i < keys.length; i += 1) {
    const v = node[keys[i]];
    if (v != null && typeof v === 'object') {
      const found = deepFindTvcIssuesBehaviorSource(v, depth + 1, maxDepth);
      if (found) return found;
    }
  }
  return null;
};

/**
 * tvcIssues behavior source: format.tvcIssuesBehavior, behaviors[].source (incl. arrays),
 * or any nested object with type tvcIssuesBehavior within the component format node.
 * @param {Object|null|undefined} format
 * @returns {Object|null}
 */
export const getTvcIssuesBehaviorSourceFromFormat = format => {
  if (format == null || typeof format !== 'object') {
    return null;
  }
  if (format.tvcIssuesBehavior != null) {
    return format.tvcIssuesBehavior;
  }
  const behaviors = format.behaviors;
  if (Array.isArray(behaviors)) {
    for (let i = 0; i < behaviors.length; i += 1) {
      const b = behaviors[i];
      const fromSource = firstTvcIssuesInSource(b === null || b === void 0 ? void 0 : b.source);
      if (fromSource) return fromSource;
      const fromSources = firstTvcIssuesInSource(b === null || b === void 0 ? void 0 : b.sources);
      if (fromSources) return fromSources;
    }
  }
  return deepFindTvcIssuesBehaviorSource(format, 0, 8);
};

/**
 * Resolve issues-load behavior for TVC section: prefer explicit prop from format spread,
 * then parse format (incl. deep search).
 * @param {Object|null|undefined} props - component props (ownProps + merged)
 * @returns {Object|null}
 */
export const resolveTvcIssuesBehaviorFromProps = props => {
  if (props == null || typeof props !== 'object') {
    return null;
  }
  if (props.tvcIssuesBehavior != null) {
    return props.tvcIssuesBehavior;
  }
  return getTvcIssuesBehaviorSourceFromFormat(props.format);
};

/**
 * Find the format subtree for this TVC section inside the full active form/report format.
 * Used when props.format lost behaviors during format evaluation but the tree still has them.
 * @param {Object|null|undefined} rootFormat
 * @param {Object|null|undefined} fragment - props.format (type, contentPath, label)
 * @returns {Object|null}
 */
export const findTvcIssuesSectionFormatNode = (rootFormat, fragment) => {
  if (rootFormat == null || fragment == null || typeof fragment !== 'object') {
    return null;
  }
  if (fragment.type !== 'tvcIssuesSection') {
    return null;
  }
  const contentPath = fragment.contentPath,
    label = fragment.label;
  const stack = [rootFormat];
  while (stack.length > 0) {
    const n = stack.pop();
    if (n != null && typeof n === 'object' && n.type === 'tvcIssuesSection' && n.contentPath === contentPath && (label === undefined || label === null || n.label === label)) {
      return n;
    }
    const ch = n === null || n === void 0 ? void 0 : n.children;
    if (Array.isArray(ch)) {
      for (let i = ch.length - 1; i >= 0; i -= 1) {
        stack.push(ch[i]);
      }
    }
  }
  return null;
};

/**
 * Full resolution for mapStateToProps: explicit/template config, then active.format tree,
 * else built-in {@link DEFAULT_TVC_ISSUES_BEHAVIOR_SOURCE} (no JSON required).
 * @param {Object} state - redux root
 * @param {Object} props - connect props (incl. reduxEntity, format)
 * @returns {Object}
 */
export const selectTvcIssuesBehavior = (state, props) => {
  var _state$entity;
  const fromProps = resolveTvcIssuesBehaviorFromProps(props);
  if (fromProps) {
    return fromProps;
  }
  const entity = props.reduxEntity;
  if (entity && (_state$entity = state[entity]) !== null && _state$entity !== void 0 && (_state$entity = _state$entity.active) !== null && _state$entity !== void 0 && _state$entity.format) {
    const match = findTvcIssuesSectionFormatNode(state[entity].active.format, props.format);
    if (match) {
      const fromTree = getTvcIssuesBehaviorSourceFromFormat(match);
      if (fromTree) {
        return fromTree;
      }
    }
  }
  return DEFAULT_TVC_ISSUES_BEHAVIOR_SOURCE;
};